import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
  Chip,
  TextField,
} from "@mui/material";
import { useDebounce } from "@uidotdev/usehooks";
import intl from "components/i18n/ReactIntlWrapper";

export type AutocompleteCustomProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> = {
  apiPromise: (searchTerm: string) => Promise<any>;
  label?: string;
  inputClassName?: string;
  getOptionLabel: (
    option: Value | AutocompleteFreeSoloValueMapping<FreeSolo>
  ) => string;
  getOptionDisabled?: (option: Value) => boolean;
  value?: Value;
  onChange?: (event: React.ChangeEvent<{}>, value: Value | null) => void;
  disabled?: boolean;
  initialText?: string;
  charLength?: number;
  multiple?: boolean;
  disableClearable?: boolean;
  autocompleteProps?: Partial<AutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo
  >>;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  error?: boolean;
  readonly?: boolean;
};

export default function TypeAhead<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(props: AutocompleteCustomProps<Value, Multiple, DisableClearable, FreeSolo>) {
  const {
    apiPromise,
    label,
    inputClassName,
    getOptionLabel,
    getOptionDisabled,
    onChange,
    disabled,
    value,
    initialText,
    autocompleteProps,
    charLength,
    multiple,
    disableClearable,
    isOptionEqualToValue = (selectedOption, option) => selectedOption.id === option.id ,
    error,
    readonly,
  } = props;

  const DEBOUNCE_TIME = 1000;
  const MAX_LENGTH = charLength ?? 3;
  
  const [internalValue, setInternalValue] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_TIME);
  const [options, setOptions] = useState<any[]>([]);
  const [noOptions, setNoOptions] =  useState<boolean>(false);
  const [autoCompleteLoading, setAutoCompleteLoading] =  useState<boolean>(false);
  const [limitTags, setLimitTags] = useState(undefined);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const translatedInitialText = initialText || intl.get('common.placeholder.typeAhead.initialText');

  useEffect(() => {
    if (debouncedSearchTerm?.length < MAX_LENGTH) {
      setOptions([]);
      return;
    } else {
      setAutoCompleteLoading(true);
      apiPromise && apiPromise(searchTerm)
        .then((response) => {
          const { data } = response?.data || {};
          // Ensure data is always an array to prevent filter errors
          const safeData = Array.isArray(data) ? data : [];
          setOptions(safeData);
          setNoOptions(safeData.length > 0 ? false : true);
        })
        .catch((error) => {
          console.error('TypeAhead API error:', error);
          setOptions([]);
          setNoOptions(true);
        })
        .finally(() => {
          setAutoCompleteLoading(false);
        });
    }
  }, [debouncedSearchTerm]);

    useEffect(() => {
      const calculateLimitTags = () => {
        if (autocompleteRef.current) {
          const parent = autocompleteRef.current.querySelector(".MuiInputBase-root");
          const parentStyles = window.getComputedStyle(parent);
          const parentWidth = parent.clientWidth - parseFloat(parentStyles.paddingLeft) - parseFloat(parentStyles.paddingRight);
  
          // Width of the input. Initial approach to hide it when unfocused instead but this breaks Autocomplete.
          const input = autocompleteRef.current.querySelector( ".MuiInputBase-input");
          const inputStyles = window.getComputedStyle(input);
          const inputWidth = parseFloat(inputStyles.paddingLeft) + parseFloat(inputStyles.paddingRight) + parseFloat(inputStyles.minWidth);
  
          // Width of the tag (the "+1" for hidden items). Can't get the size if it doesn't exist, so we use 2x the font size of the input as a heuristic.
          const tagWidth = parseFloat(inputStyles.fontSize) * 2;
  
          let totalChipWidth = inputWidth + tagWidth;
          let chipsThatFit = 0;
  
          const chips = autocompleteRef.current.querySelectorAll(".MuiChip-root");
          const optionsLength = options?.length;

          if(chips.length > 0) {
            chips.forEach((chip) => {
              const chipWidth =
                chip.clientWidth + parseInt(window.getComputedStyle(chip).marginRight) + parseInt(window.getComputedStyle(chip).marginLeft);
              if (totalChipWidth + chipWidth <= parentWidth) {
                totalChipWidth += chipWidth;
                chipsThatFit += 1;
              }
            });
            setLimitTags(chipsThatFit);
          }else{
            setLimitTags(undefined);
          }
        }
      }
      calculateLimitTags();
      window.addEventListener("resize", calculateLimitTags);
      return () => {
        window.removeEventListener("resize", calculateLimitTags);
      };
    }, [value, isFocused]);

  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  const getNoOptionsText = (searchTerm: string, options: any[]) => {
    if (searchTerm?.length === 0){
      return translatedInitialText;
    }
    if (searchTerm?.length > 0 && searchTerm?.length < MAX_LENGTH) {
      return intl.get('placeholder.enterAtLeastXChars', { count: MAX_LENGTH });
    }
    if (searchTerm?.length >= MAX_LENGTH && noOptions && options?.length === 0) {
      return intl.get('placeholder.noOptions');
    }
    return intl.get('spinner') 
   };

  return (
    multiple ?  
    <Autocomplete
      ref={autocompleteRef}
      disableClearable={disableClearable ?? false}
      disablePortal
      open={isFocused}
      onOpen={() => setIsFocused(true)}
      isOptionEqualToValue={isOptionEqualToValue}
      onClose={(event, reason) => {
        if (reason === "blur") {
          setIsFocused(false);
        }
      }}
      multiple={multiple?? false}
      renderValue={(value, getValueProps) => {
        if (value.length === 0) {
          return translatedInitialText;
        }
        return value.map((option, index) => (
          <Chip
            key={`autocomplete-option-${index}`}
            label={getOptionLabel(option)}
            color="primary"
            variant="outlined"
            sx={{
              visibility: limitTags ? "visible" : "hidden",
            }}
            {...getValueProps({ index })}
          />
        ));
      }}
      limitTags={limitTags}
      inputValue={searchTerm || ""}
      onInputChange={(event, newInputValue) => {
        setSearchTerm(newInputValue || '');
      }}
      options={Array.isArray(options) ? options : []}
      loading={autoCompleteLoading}
      loadingText={intl.get('spinner')}
      value={internalValue || ""}
      onFocus={() => {
        const optionLabel = internalValue ? getOptionLabel(internalValue) : null;
        setSearchTerm(optionLabel || "");
      }}
      onChange={(event, newValue) => {
        setInternalValue(newValue);
        onChange(event, newValue as Value | null);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          className={`${inputClassName}`}
          size={autocompleteProps?.size || "small"}
          label={label}
          variant="outlined"
        />
      )}
      getOptionLabel={(option: Value) => {
        return getOptionLabel(option) || ''
      }}
      getOptionDisabled={getOptionDisabled}
      disabled={disabled}
      readOnly={readonly}
      noOptionsText = {getNoOptionsText(searchTerm, options)}
      {...autocompleteProps}
    />
    :
    <Autocomplete
      inputValue={searchTerm || ""}
        onInputChange={(event, newInputValue) => {
          setSearchTerm(newInputValue || '');
        }}
        options={Array.isArray(options) ? options : []}
        loading={autoCompleteLoading}
        loadingText={intl.get('spinner')}
        value={internalValue || ""}
        onFocus={() => {
          const optionLabel = internalValue ? getOptionLabel(internalValue) : null;
          setSearchTerm(optionLabel || "");
        }}
        onChange={(event, newValue) => {
          setInternalValue(newValue);
          onChange(event, newValue as Value | null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className={`${inputClassName}`}
            size={autocompleteProps?.size || "small"}
            label={label}
            variant="outlined"
          />
        )}
        getOptionLabel={(option: Value) => {
          return getOptionLabel(option) || ''
        }}
        getOptionDisabled={getOptionDisabled}
        disabled={disabled}
        readOnly={readonly}
        noOptionsText = {getNoOptionsText(searchTerm, options)}
        {...autocompleteProps}
    />
  );
}
